import { ApolloQueryResult } from 'apollo-client'
import { ButtonProps, IconProps } from 'bold-ui'
import { ButtonAction } from 'bold-ui/lib/components/Modal/auto/ModalAuto'
import { isAfter, isBefore, parseISO, startOfDay } from 'date-fns'
import {
  EstrategiaVacinacao,
  FaixaEtariaVacinacaoQuery,
  FaixaEtariaVacinacaoQueryVariables,
  LoteHasCadastroQuery,
  LoteHasCadastroQueryVariables,
  RegistroAplicado,
  TipoRegistroVacinacaoEnum,
} from 'graphql/types.generated'
import { calculateAge } from 'util/date/calculateAge'
import { MetaRoot } from 'util/metaPath'
import { covidImunosRecord } from 'view/lote-imunobiologico/insert/LoteImunologicoTooltipAddForm'

import { createVacinacaoErrorModal } from '../detail/vacinacao/VacinacaoCalendarioView'
import { CidadaoAtendimento } from '../types/CidadaoAtendimento'
import { RegistroVacinacaoFormModel } from './model'
import { SubModalVacinacaoOptions } from './VacinacaoModal'
import { ageInDays, getRegistroExistente } from './vacinacaoUtils'

export interface ValidateRegistroVacinaError {
  hasError: boolean
  title?: string
  subTitle?: string
  actions?: ButtonAction[]
  type?: ButtonProps['kind']
  icon?: IconProps['icon']
}

export interface ValidateRegistroVacinaInput {
  cidadao?: CidadaoAtendimento
  tipoRegistroVacinacao?: TipoRegistroVacinacaoEnum
  imunobiologicoId?: ID
  doseId?: ID
  estrategiaVacinacao?: EstrategiaVacinacao
  isRegistroAnterior?: boolean
  dataAtendimento?: number
  dataAprazamento?: string
  loteDataValidade?: string
  registrosAplicados?: RegistroAplicado[]
  registrosAplicadosAtendimento?: RegistroVacinacaoFormModel[]
  //idUnicoRegistro é utilizado para validar, ou não, registros em edição
  idUnicoRegistro?: ID
  handleOnSubmit?(): void
  refetchFaixaEtaria?: (
    variables?: FaixaEtariaVacinacaoQueryVariables
  ) => Promise<ApolloQueryResult<FaixaEtariaVacinacaoQuery>>
  handleSubModalVacinacaoClose?(): void
  tipoModal?: SubModalVacinacaoOptions
  refetchLoteHasCadastro?: (
    variables?: LoteHasCadastroQueryVariables
  ) => Promise<ApolloQueryResult<LoteHasCadastroQuery>>
  isCadastrarNovoLote?: boolean
  fabricanteNome?: string
  loteNome?: string
}

export interface VacinacaoRegistrosAplicadosFilterInput {
  registrosAplicados?: RegistroAplicado[]
  registrosAplicadosAtendimento?: RegistroVacinacaoFormModel[]
  imunobiologicoId: ID
  doseId?: ID
  isRegistroAnterior?: boolean
  isAprazamento?: boolean
  tipoRegistro?: TipoRegistroVacinacaoEnum
  filterDuasListas?: boolean
  idUnicoRegistro?: ID
}

export interface ModaisVacinacao {
  impeditivos: ValidateRegistroVacinaError[]
  naoImpeditivos: ValidateRegistroVacinaError[]
  handleOnSubmit?(): void
}

export enum ValidacoesVacinacaoEnum {
  FEBRE_AMARELA,
  DTPA_ADULTO,
  GESTANTE,
  BCG,
}

export const imunosComValidacoesIds: Record<ValidacoesVacinacaoEnum, string[]> = {
  [ValidacoesVacinacaoEnum.FEBRE_AMARELA]: ['14'],
  [ValidacoesVacinacaoEnum.DTPA_ADULTO]: ['57'],
  [ValidacoesVacinacaoEnum.GESTANTE]: ['15', '14', '24', '67', '82', '36', '85', '88', '56'],
  [ValidacoesVacinacaoEnum.BCG]: ['15'],
}

const DOSE_UNICA_ID = '9'
const PRIMEIRA_DOSE_ID = '1'

export const submitImunobiologicoValidate = async (input: ValidateRegistroVacinaInput): Promise<ModaisVacinacao> => {
  const {
    cidadao,
    tipoRegistroVacinacao,
    imunobiologicoId,
    doseId,
    estrategiaVacinacao,
    isRegistroAnterior,
    dataAtendimento,
    dataAprazamento,
    refetchFaixaEtaria,
    loteDataValidade,
    registrosAplicados,
    registrosAplicadosAtendimento,
    idUnicoRegistro,
    handleOnSubmit,
    refetchLoteHasCadastro,
    isCadastrarNovoLote,
    loteNome,
    fabricanteNome,
  } = input

  let errors: ValidateRegistroVacinaError[] = []
  let errorsImpeditivos: ValidateRegistroVacinaError[] = []

  if (tipoRegistroVacinacao === TipoRegistroVacinacaoEnum.APLICACAO && !isRegistroAnterior) {
    errors.push(
      await validateFaixaEtaria(
        refetchFaixaEtaria,
        cidadao.dataNascimento,
        dataAtendimento,
        dataAprazamento,
        imunobiologicoId,
        doseId,
        estrategiaVacinacao.id,
        idUnicoRegistro
      )
    )

    if (covidImunosRecord[imunobiologicoId]) {
      errorsImpeditivos.push(validateCovid(cidadao.cpf || cidadao.cns))
    }

    if (imunosComValidacoesIds[ValidacoesVacinacaoEnum.FEBRE_AMARELA][0] === imunobiologicoId) {
      errorsImpeditivos.push(validateFebreAmarelaDoseUnica(registrosAplicados, registrosAplicadosAtendimento, doseId))
      errorsImpeditivos.push(validateFebreAmarelaDoseUm(registrosAplicados, doseId, registrosAplicadosAtendimento))
    }

    if (isCadastrarNovoLote) {
      errorsImpeditivos.push(
        await validateLoteHasCadastro(
          refetchLoteHasCadastro,
          loteNome,
          fabricanteNome,
          imunobiologicoId,
          loteDataValidade
        )
      )
    }

    errorsImpeditivos.push(
      validateImunobiologicoJaAplicado(registrosAplicadosAtendimento, imunobiologicoId, idUnicoRegistro)
    )

    errors.push(validateDoseJaAplicada(doseId, imunobiologicoId, registrosAplicados, idUnicoRegistro))
    errors.push(validateDataValidade(loteDataValidade))
  }

  if (isRegistroAnterior) {
    if (covidImunosRecord[imunobiologicoId]) {
      errorsImpeditivos.push(validateCovid(cidadao.cpf || cidadao.cns))
    }

    if (imunosComValidacoesIds[ValidacoesVacinacaoEnum.FEBRE_AMARELA][0] === imunobiologicoId) {
      errorsImpeditivos.push(validateFebreAmarelaDoseUnica(registrosAplicados, registrosAplicadosAtendimento, doseId))
      errorsImpeditivos.push(validateFebreAmarelaDoseUm(registrosAplicados, doseId, registrosAplicadosAtendimento))
    }

    errors.push(validateDoseJaAplicada(doseId, imunobiologicoId, registrosAplicados, idUnicoRegistro))
  }

  if (tipoRegistroVacinacao === TipoRegistroVacinacaoEnum.APRAZAMENTO) {
    errorsImpeditivos.push(
      validateDoseJaAprazada(doseId, imunobiologicoId, idUnicoRegistro, registrosAplicadosAtendimento)
    )
    errors.push(
      await validateFaixaEtaria(
        refetchFaixaEtaria,
        cidadao.dataNascimento,
        dataAtendimento,
        dataAprazamento,
        imunobiologicoId,
        doseId,
        estrategiaVacinacao.id,
        idUnicoRegistro,
        true
      )
    )
  }

  return {
    impeditivos: errorsImpeditivos.filter((error) => error),
    naoImpeditivos: errors.filter((error) => error),
    handleOnSubmit,
  }
}

const validateCovid = (cpfCns: string): ValidateRegistroVacinaError => {
  if (!cpfCns) {
    return {
      hasError: true,
      title: 'Não é possível salvar o registro',
      subTitle: 'Para registrar as vacinas COVID-19 o CPF / CNS do cidadão deve ser preenchido no cadastro do cidadão.',
      actions: [{ label: 'OK', kind: 'primary' }],
      type: 'danger',
      icon: 'exclamationTriangleOutline',
    }
  }
}

const validateDataValidade = (loteDataValidade: string): ValidateRegistroVacinaError => {
  if (isBefore(parseISO(loteDataValidade), startOfDay(new Date()))) {
    return {
      hasError: true,
      title: 'Deseja confirmar o registro?',
      subTitle: 'A data de validade do lote de imunobiológico sendo cadastrado está vencida.',
      type: 'primary',
      icon: 'questionMarkFilled',
    }
  }
}

const validateFaixaEtaria = async (
  refetchFaixaEtaria: (
    variables?: FaixaEtariaVacinacaoQueryVariables
  ) => Promise<ApolloQueryResult<FaixaEtariaVacinacaoQuery>>,
  dataNascimento: string,
  dataAtendimento: number,
  dataAprazamento: string,
  imunobiologicoId: ID,
  doseId: ID,
  estrategiaId: ID,
  idUnicoRegistro: ID,
  isAprazamento: boolean = false
): Promise<ValidateRegistroVacinaError> => {
  let error: ValidateRegistroVacinaError = undefined

  const aplicacaoEmEdicacao = !isAprazamento && idUnicoRegistro

  if (aplicacaoEmEdicacao) {
    return error
  }

  const age = calculateAge(dataNascimento, isAprazamento ? dataAprazamento : dataAtendimento)

  const idadeCalculoFaixa = ageInDays(age)

  const result = await refetchFaixaEtaria({
    input: {
      imunobiologicoId,
      doseId,
      estrategiaId,
    },
  }).catch(() => {
    error = {
      hasError: true,
      title: `Deseja confirmar o registro?`,
      subTitle: 'Não foi possível verificar se esta dose de imunobiológico está dentro da faixa etária recomendada.',
      type: 'primary',
      icon: 'questionMarkOutline',
    }
  })

  if (result) {
    const faixaEtaria = result.data?.faixaEtariaVacinacao

    if (
      faixaEtaria &&
      (isBefore(idadeCalculoFaixa, faixaEtaria.diasInicio) || isAfter(idadeCalculoFaixa, faixaEtaria.diasFim))
    ) {
      const msgAplicacao = `Esta dose de imunobiológico não é recomendada para cidadãos fora da faixa etária: <br/><br/>
        ${faixaEtaria.descricao}.<br/><br/>
        Deseja confirmar o registro da dose?
        `

      const msgAprazamento = `Nesta data de aprazamento o cidadão estará fora da faixa etária recomendada para receber esta dose de imunobiológico: <br/><br/>
        ${faixaEtaria.descricao}.<br/><br/>
        Deseja confirmar o registro da dose?`

      error = {
        hasError: true,
        title: `Deseja confirmar o registro de ${isAprazamento ? 'aprazamento ' : 'aplicação'}?`,
        subTitle: isAprazamento ? msgAprazamento : msgAplicacao,
        type: 'primary',
        icon: 'questionMarkOutline',
      }
    }
  }

  return error
}

const validateLoteHasCadastro = async (
  loteHasCadastro: (variables?: LoteHasCadastroQueryVariables) => Promise<ApolloQueryResult<LoteHasCadastroQuery>>,
  loteNome: string,
  nomeFabricante: string,
  idImunobiologico: string,
  validade: string
): Promise<ValidateRegistroVacinaError> => {
  let error: ValidateRegistroVacinaError = undefined

  const result = await loteHasCadastro({
    input: {
      lote: loteNome,
      nomeFabricante,
      idImunobiologico,
      validade,
    },
  }).catch(() => {
    error = {
      hasError: true,
      title: `Deseja confirmar o registro?`,
      subTitle: 'Não foi possível verificar se este lote de imunobiológico já está cadastrado.',
      type: 'primary',
      icon: 'questionMarkOutline',
    }
  })

  if (result && result.data?.loteHasCadastro !== null) {
    error = {
      hasError: true,
      title: 'Não é possível salvar o registro',
      subTitle: 'Este lote de imunobiológico já está cadastrado.',
      actions: [{ label: 'OK', kind: 'primary' }],
      type: 'danger',
      icon: 'questionMarkOutline',
    }
  }

  return error
}

const validateFebreAmarelaDoseUnica = (
  registrosAplicados: RegistroAplicado[],
  registrosAplicadosAtendimento: RegistroVacinacaoFormModel[],
  doseId: string
): ValidateRegistroVacinaError => {
  const registroExistente = getRegistroExistente({
    registrosAplicados,
    registrosAplicadosAtendimento,
    imunobiologicoId: imunosComValidacoesIds[ValidacoesVacinacaoEnum.FEBRE_AMARELA][0],
    tipoRegistro: TipoRegistroVacinacaoEnum.APLICACAO,
    doseId: PRIMEIRA_DOSE_ID,
    isAprazamento: false,
    isRegistroAnterior: true,
    filterDuasListas: true,
  })

  if (registroExistente && doseId === DOSE_UNICA_ID) {
    return {
      hasError: true,
      title: 'Não é possível salvar o registro',
      subTitle:
        'O cidadão já recebeu a D1 (1ª Dose) deste imunobiológico, que é equivalente à DU (Única). <br/><br/> Para visualizar o histórico da D1, acesse o Calendário vacinal.',
      actions: [{ label: 'OK', kind: 'primary' }],
      type: 'danger',
      icon: 'exclamationTriangleOutline',
    }
  }
}

const validateImunobiologicoJaAplicado = (
  registrosAplicadosAtendimento: RegistroVacinacaoFormModel[],
  imunobiologicoId: string,
  idUnicoRegistro: ID
): ValidateRegistroVacinaError => {
  const registroExistente = getRegistroExistente({
    registrosAplicadosAtendimento,
    imunobiologicoId,
    tipoRegistro: TipoRegistroVacinacaoEnum.APLICACAO,
    isRegistroAnterior: false,
    idUnicoRegistro,
  })

  if (registroExistente) {
    return {
      hasError: true,
      title: 'Não é possível salvar o registro',
      subTitle: 'Este Imunobiológico já está cadastrado.',
      actions: [{ label: 'OK', kind: 'primary' }],
      type: 'danger',
      icon: 'exclamationTriangleOutline',
    }
  }
}

const validateDoseJaAplicada = (
  doseId: string,
  imunobiologicoId: string,
  registrosAplicados: RegistroAplicado[],
  idUnicoRegistro: ID
): ValidateRegistroVacinaError => {
  const registroExistente = getRegistroExistente({
    registrosAplicados,
    imunobiologicoId,
    doseId,
    isAprazamento: false,
  }) as RegistroAplicado

  if (registroExistente && !idUnicoRegistro) {
    return {
      hasError: true,
      title: 'Deseja confirmar o registro?',
      subTitle: `O cidadão já recebeu a ${registroExistente.doseSigla} do imunobiológico ${registroExistente.imunobiologicoNome}.`,
      type: 'primary',
      icon: 'questionMarkFilled',
    }
  }
}

const validateDoseJaAprazada = (
  doseId: string,
  imunobiologicoId: string,
  idUnicoRegistro: string,
  registrosAplicadosAtendimento: RegistroVacinacaoFormModel[]
): ValidateRegistroVacinaError => {
  const registroExistente = getRegistroExistente({
    registrosAplicadosAtendimento,
    imunobiologicoId,
    doseId,
    tipoRegistro: TipoRegistroVacinacaoEnum.APRAZAMENTO,
    idUnicoRegistro,
  })

  if (registroExistente) {
    return {
      hasError: true,
      title: 'Não é possível salvar o registro',
      subTitle: `Esta dose deste imunobiológico já possui um aprazamento cadastrado neste atendimento. Um novo aprazamento poderá ser registrado em um atendimento posterior.`,
      actions: [{ label: 'OK', kind: 'primary' }],
      type: 'danger',
      icon: 'exclamationTriangleOutline',
    }
  }
}

const validateFebreAmarelaDoseUm = (
  registrosAplicados: RegistroAplicado[],
  doseClicadaId: string,
  registrosAplicadosAtendimento: RegistroVacinacaoFormModel[]
): ValidateRegistroVacinaError => {
  const registroExistente = getRegistroExistente({
    filterDuasListas: true,
    registrosAplicados,
    registrosAplicadosAtendimento,
    imunobiologicoId: imunosComValidacoesIds[ValidacoesVacinacaoEnum.FEBRE_AMARELA][0],
    doseId: DOSE_UNICA_ID,
    isRegistroAnterior: true,
    tipoRegistro: TipoRegistroVacinacaoEnum.APLICACAO,
  })

  if (doseClicadaId === PRIMEIRA_DOSE_ID && registroExistente) {
    return {
      hasError: true,
      title: 'Não é possível registrar esse imunobiológico',
      subTitle:
        'O cidadão já recebeu a DU (Única) deste imunobiológico, que é equivalente à D1 (1ª Dose).<br/><br/> Para visualizar o histórico da DU, acesse Outros imunobiológicos.',
      actions: [{ label: 'OK', kind: 'primary' }],
      type: 'danger',
      icon: 'exclamationTriangleOutline',
    }
  }
}

const validateImunoContraIndicadoGestante = (input: ValidateRegistroVacinaInput): ValidateRegistroVacinaError => {
  const { imunobiologicoId, tipoModal, idUnicoRegistro, handleSubModalVacinacaoClose } = input

  if (
    tipoModal === 'APLICACAO' &&
    idUnicoRegistro === undefined &&
    imunosComValidacoesIds[ValidacoesVacinacaoEnum.GESTANTE].find((id) => id === input.imunobiologicoId)
  ) {
    const subTitle = covidImunosRecord[imunobiologicoId]
      ? 'Essa vacina não é recomendada para gestantes!'
      : 'Gestantes e mulheres amamentando devem receber a dose desta vacina somente em casos de surtos, epidemias ou com recomendação médica!'

    return {
      hasError: true,
      title: `Deseja aplicar esta vacina?`,
      subTitle: subTitle,
      actions: [
        { label: 'Não', onClick: () => handleSubModalVacinacaoClose() },
        { label: 'Sim', kind: 'primary' },
      ],
      type: 'primary',
      icon: 'questionMarkFilled',
    }
  }
}

export const validateSubModal = (input: ValidateRegistroVacinaInput, isGestante: boolean): ModaisVacinacao => {
  let errors: ValidateRegistroVacinaError[] = []
  let errorsImpeditivos: ValidateRegistroVacinaError[] = []

  if (
    imunosComValidacoesIds[ValidacoesVacinacaoEnum.FEBRE_AMARELA][0] === input.imunobiologicoId &&
    input.tipoModal !== 'APRAZAMENTO'
  ) {
    errorsImpeditivos.push(
      validateFebreAmarelaDoseUm(input.registrosAplicados, input.doseId, input.registrosAplicadosAtendimento)
    )
  }

  if (isGestante) {
    errors.push(validateImunoContraIndicadoGestante(input))
  }

  return {
    impeditivos: errorsImpeditivos.filter((error) => error),
    naoImpeditivos: errors.filter((error) => error),
    handleOnSubmit: input.handleOnSubmit,
  }
}

export const validateFinalizarAtendimento = (input: ValidateRegistroVacinaInput) => {
  let error: ValidateRegistroVacinaError = { hasError: false }

  if (input.registrosAplicadosAtendimento.length === 0) {
    error = {
      hasError: true,
      title: 'Para finalizar o atendimento é necessário preencher no mínimo um dos tipos de registro:',
      subTitle: ' - Aplicação<br>- Aprazamento<br>- Registro Anterior<br>',
      actions: [{ label: 'OK', kind: 'primary' }],
      type: 'danger',
      icon: 'exclamationTriangleOutline',
    }
  }

  return error
}

export const validateContraIndicacaoAplicacaoOutrosImunos = (
  imunobiologicoId: string,
  formChange: (name: string, value?: any) => void,
  meta: MetaRoot<RegistroVacinacaoFormModel>
) => {
  const error = validateImunoContraIndicadoGestante({
    imunobiologicoId: imunobiologicoId,
    tipoModal: 'APLICACAO',
    handleSubModalVacinacaoClose: () => {
      formChange(meta.outrosImunosImunobiologico.absolutePath(), null)
    },
  })

  if (error?.hasError) {
    createVacinacaoErrorModal(error)
  }
}

// Trata a sobreposição de erros
export const checkErrors = (errors: ModaisVacinacao) => {
  let counter = 0

  const submitModal = (final: boolean) => {
    if (final) {
      errors.handleOnSubmit()
    } else {
      counter++
      createVacinacaoErrorModal(errors.naoImpeditivos[counter], () =>
        submitModal(counter === errors.naoImpeditivos.length - 1)
      )
    }
  }

  if (errors.impeditivos.length > 0) {
    createVacinacaoErrorModal(errors.impeditivos[0])
  } else if (errors.naoImpeditivos.length > 0) {
    createVacinacaoErrorModal(errors.naoImpeditivos[counter], () =>
      submitModal(counter === errors.naoImpeditivos.length - 1)
    )
  }
}
